import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
} from "react-bootstrap";
import { common } from "../js/common";

interface AccountData {
  id: number;
  userName: string;
  email: string;
  role: string;
  createdOn: string;
}

function AccountPage() {
  const [account, setAccount] = useState<AccountData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [newUsername, setNewUsername] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    async function fetchAccount() {
      try {
        const sessionRes = await common.fetchRestAPI("sessions", "GET");
        if (!sessionRes.ok) {
          setError("Not authenticated");
          setLoading(false);
          return;
        }
        const session = await sessionRes.json();

        const accountRes = await common.fetchRestAPI(
          `accounts/${session.id}`,
          "GET",
        );
        if (!accountRes.ok) {
          setError("Failed to load account");
          setLoading(false);
          return;
        }
        const accountData: AccountData = await accountRes.json();
        setAccount(accountData);
        setNewUsername(accountData.userName);
        setNewEmail(accountData.email ?? "");
      } catch {
        setError("Failed to load account");
      }
      setLoading(false);
    }
    fetchAccount();
  }, []);

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!account) return;

    if (newPassword && newPassword !== confirmPassword) {
      setSaveError("Passwords do not match.");
      return;
    }
    if (newPassword && newPassword.length < 8) {
      setSaveError("Password must be at least 8 characters.");
      return;
    }

    const body: any = {};
    if (newUsername !== account.userName) body.userName = newUsername;
    if (newEmail.trim() !== account.email) body.email = newEmail.trim();
    if (newPassword) body.password = newPassword;

    if (Object.keys(body).length === 0) {
      setSaveError("No changes to save.");
      return;
    }

    setSaving(true);
    setSaveError("");
    setSaveMessage("");

    try {
      const res = await common.fetchRestAPI(
        `accounts/${account.id}`,
        "PUT",
        body,
      );

      if (res.status === 409) {
        setSaveError("That username or email is already taken.");
        setSaving(false);
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        setSaveError(data?.error || "Failed to update account.");
        setSaving(false);
        return;
      }

      setSaveMessage("Account updated successfully!");
      setNewPassword("");
      setConfirmPassword("");
      setAccount({
        ...account,
        userName: body.userName ?? account.userName,
        email: body.email ?? account.email,
      });
    } catch {
      setSaveError("An error occurred updating your account.");
    }
    setSaving(false);
  };

  const handleDeleteAccount = async () => {
    if (
      !account ||
      !window.confirm(
        "Are you sure you want to delete your account? This cannot be undone.",
      )
    )
      return;

    try {
      const res = await common.fetchRestAPI(
        `accounts/${account.id}`,
        "DELETE",
      );
      if (res.ok) {
        window.location.href = "/";
      } else {
        alert("Failed to delete account.");
      }
    } catch {
      alert("An error occurred deleting your account.");
    }
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center p-5">
        <Spinner animation="border" size="sm" />
      </Container>
    );
  }

  if (error || !account) {
    return (
      <Container className="p-5">
        <p className="alert alert-warning text-center">
          {error || "Failed to load account"}
        </p>
      </Container>
    );
  }

  return (
    <Container className="p-4">
      <Row className="justify-content-center">
        <Col lg={6}>
          <h2 className="mb-4">Account Settings</h2>
          <Card className="p-4 mb-3">
            <p className="text-muted mb-1">
              Role: <strong>{account.role}</strong>
            </p>
            <p className="text-muted mb-3">
              Member since:{" "}
              {new Date(account.createdOn).toLocaleDateString()}
            </p>

            <Form onSubmit={handleSave}>
              <Form.Group className="mb-3">
                <Form.Label>Username</Form.Label>
                <Form.Control
                  type="text"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  maxLength={55}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  maxLength={255}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  New Password (leave blank to keep current)
                </Form.Label>
                <Form.Control
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="New password"
                  minLength={8}
                />
              </Form.Group>

              {newPassword && (
                <Form.Group className="mb-3">
                  <Form.Label>Confirm New Password</Form.Label>
                  <Form.Control
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm password"
                    minLength={8}
                    required
                  />
                </Form.Group>
              )}

              <Button type="submit" variant="primary" disabled={saving}>
                {saving ? "Saving..." : "Save Changes"}
              </Button>

              {saveMessage && (
                <div className="alert alert-success mt-3">{saveMessage}</div>
              )}
              {saveError && (
                <div className="alert alert-danger mt-3">{saveError}</div>
              )}
            </Form>
          </Card>

          <Card className="p-4 border-danger">
            <h5 className="text-danger">Danger Zone</h5>
            <p className="text-muted">
              Deleting your account is permanent and cannot be undone.
            </p>
            <Button variant="outline-danger" onClick={handleDeleteAccount}>
              Delete Account
            </Button>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default AccountPage;
